import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Typography,
  InputAdornment,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';

import { caixaFinanceiroService, CaixaFinanceiro } from '../../services/caixaFinanceiroService';
import { formatarValor, formatarValorOnInput } from '../../utils/formatToView';

interface CaixaFinanceiroFormProps {
  caixa?: CaixaFinanceiro | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  nome: string;
  tipo_vinculo: 'escritorio' | 'usuario' | '';
  vinculo_id: number | null;
  saldo_inicial: number;
  observacoes: string;
}

interface VinculoOption {
  id: number;
  nome: string;
  label: string;
}

const CaixaFinanceiroForm: React.FC<CaixaFinanceiroFormProps> = ({
  caixa,
  onSuccess,
  onCancel
}) => {
  // Estados do formulário
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    tipo_vinculo: '',
    vinculo_id: null,
    saldo_inicial: 0,
    observacoes: ''
  });

  // Estados de opções para vinculação
  const [escritorios, setEscritorios] = useState<VinculoOption[]>([]);
  const [usuarios, setUsuarios] = useState<VinculoOption[]>([]);

  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Estado de validação
  const [errors, setErrors] = useState<Partial<FormData>>({});

  // Estado para formatação de moeda
  const [saldoFormatado, setSaldoFormatado] = useState('0,00');

  const isEditing = !!caixa;
 
  // Função para converter string formatada para número
  const parsearMoeda = (valorFormatado: string): number => {
    // Remove todos os caracteres que não são números, vírgula ou ponto
    const numeroLimpo = valorFormatado.replace(/[^\d,]/g, '');
    // Substitui vírgula por ponto para conversão
    const numeroComPonto = numeroLimpo.replace(',', '.');
    const numero = parseFloat(numeroComPonto) || 0;
    return numero;
  };

  // Handler para mudança do campo saldo_inicial
  const handleSaldoChange = (valor: string) => {
    const valorFormatado = formatarValorOnInput(valor);
    const valorNumerico = parsearMoeda(valorFormatado);
    
    setSaldoFormatado(valorFormatado);
    setFormData(prev => ({ ...prev, saldo_inicial: valorNumerico }));
  };

  useEffect(() => {
    loadOptions();
    if (caixa) {
      const saldoInicial = typeof caixa.saldo_inicial === 'number' ? caixa.saldo_inicial : 0;
      setFormData({
        nome: caixa.nome,
        tipo_vinculo: caixa.tipo_vinculo,
        vinculo_id: caixa.escritorio_id || caixa.usuario_id || null,
        saldo_inicial: saldoInicial,
        observacoes: caixa.observacoes || ''
      });
      setSaldoFormatado(formatarValor(saldoInicial));
    }
  }, [caixa]);

  // Recarregar opções quando o tipo de vinculação mudar
  useEffect(() => {
    if (formData.tipo_vinculo) {
      // Verificar se as opções para o tipo selecionado já estão carregadas
      const options = getVinculoOptions();
      if (options.length === 0) {
        loadOptions();
      }
    }
  }, [formData.tipo_vinculo]);

  const loadOptions = async () => {
    try {
      setLoadingOptions(true);
      
      // Carregar opções em paralelo
      const [escritoriosResponse, usuariosResponse] = await Promise.all([
        caixaFinanceiroService.getEscritorios(),
        caixaFinanceiroService.getUsuarios()
      ]);

      // Processar escritórios
      if (escritoriosResponse && escritoriosResponse.data && Array.isArray(escritoriosResponse.data)) {
        const escritoriosProcessados = escritoriosResponse.data.map((escritorio: any) => ({
          id: escritorio.id,
          nome: escritorio.identificacao,
          label: `${escritorio.nome_fantasia} - ${escritorio.cidade}/${escritorio.estado}`
        }));
        setEscritorios(escritoriosProcessados);
      } else {
        setEscritorios([]);
      }

      // Processar usuários
      if (usuariosResponse && usuariosResponse.data && Array.isArray(usuariosResponse.data)) {
        const usuariosProcessados = usuariosResponse.data.map((usuario: any) => ({
          id: usuario.id,
          nome: usuario.nome,
          label: `${usuario.nome} (${usuario.usuario})`
        }));
        setUsuarios(usuariosProcessados);
      } else {
        setUsuarios([]);
      }

    } catch (err) {
      console.error('Erro ao carregar opções:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }

    // Resetar vinculo_id quando tipo_vinculo mudar
    if (field === 'tipo_vinculo') {
      setFormData(prev => ({
        ...prev,
        vinculo_id: null
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.tipo_vinculo) {
      newErrors.tipo_vinculo = 'Tipo de vinculação é obrigatório' as any;
    }

    if (formData.tipo_vinculo && !formData.vinculo_id) {
      newErrors.vinculo_id = 'Vinculação é obrigatória para o tipo selecionado' as any;
    }

    if (formData.saldo_inicial < 0) {
      newErrors.saldo_inicial = 'Saldo inicial deve ser um valor válido maior ou igual a zero' as any;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData: any = {
        nome: formData.nome.trim(),
        tipo_vinculo: formData.tipo_vinculo,
        saldo_inicial: formData.saldo_inicial,
        observacoes: formData.observacoes.trim() || undefined
      };

      // Adicionar campo de vinculação específico
      if (formData.tipo_vinculo === 'escritorio' && formData.vinculo_id) {
        submitData.escritorio_id = formData.vinculo_id;
      } else if (formData.tipo_vinculo === 'usuario' && formData.vinculo_id) {
        submitData.usuario_id = formData.vinculo_id;
      }

      let response;
      if (isEditing) {
        // Para edição, só permite alterar alguns campos
        const editData = {
          nome: submitData.nome,
          observacoes: submitData.observacoes
        };
        response = await caixaFinanceiroService.updateCaixa(caixa!.id, editData);
      } else {
        response = await caixaFinanceiroService.createCaixa(submitData);
      }

      if (response) {
        onSuccess();
      } else {
        throw new Error('Erro ao salvar caixa');
      }

    } catch (err: any) {
      console.error('Erro ao salvar caixa:', err);
      setError(err.message || 'Erro ao salvar caixa');
    } finally {
      setLoading(false);
    }
  };

  const getVinculoOptions = (): VinculoOption[] => {
    let options: VinculoOption[] = [];
    
    switch (formData.tipo_vinculo) {
      case 'escritorio':
        options = escritorios || [];
        break;
      case 'usuario':
        options = usuarios || [];
        break;
      default:
        options = [];
    }
    
    return options;
  };

  const getVinculoPlaceholder = (): string => {
    switch (formData.tipo_vinculo) {
      case 'escritorio':
        return 'Selecione um escritório';
      case 'usuario':
        return 'Selecione um usuário';      
      default:
        return 'Selecione o tipo primeiro';
    }
  };

  const selectedVinculo = getVinculoOptions().find(option => option.id === formData.vinculo_id);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Nome */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Nome do Caixa"
            value={formData.nome}
            onChange={(e) => handleInputChange('nome', e.target.value)}
            error={!!errors.nome}
            helperText={errors.nome}
            placeholder="Ex: Caixa São Paulo Centro"
          />
        </Grid>

        {/* Tipo de Vinculação - Oculto para caixa default */}
        {!(isEditing && caixa?.is_default) && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required error={!!errors.tipo_vinculo}>
              <InputLabel>Tipo de Vinculação</InputLabel>
              <Select
                value={formData.tipo_vinculo}
                label="Tipo de Vinculação"
                onChange={(e) => handleInputChange('tipo_vinculo', e.target.value)}
                disabled={isEditing} // Não permite alterar tipo em edição
              >
                <MenuItem value="escritorio">Escritório</MenuItem>
                <MenuItem value="usuario">Usuário</MenuItem>
              </Select>
            </FormControl>
            {errors.tipo_vinculo && (
              <Typography variant="caption" color="error">
                {errors.tipo_vinculo}
              </Typography>
            )}
          </Grid>
        )}

        {/* Vinculação Específica - Oculto para caixa default */}
        {formData.tipo_vinculo && !(isEditing && caixa?.is_default) && (
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={getVinculoOptions()}
              getOptionLabel={(option) => option.label}
              value={selectedVinculo || null}
              onChange={(event, newValue) => {
                handleInputChange('vinculo_id', newValue?.id || null);
              }}
              disabled={!formData.tipo_vinculo || isEditing || loadingOptions}
              loading={loadingOptions}
              noOptionsText={
                loadingOptions 
                  ? "Carregando opções..." 
                  : "Nenhuma opção encontrada"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={!!formData.tipo_vinculo}
                  label="Vinculação"
                  placeholder={getVinculoPlaceholder()}
                  error={!!errors.vinculo_id}
                  helperText={
                    errors.vinculo_id || 
                    (loadingOptions ? "Carregando opções..." : `${getVinculoOptions().length} opções disponíveis`)
                  }
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1">
                      {option.nome}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {option.id}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Grid>
        )}

        {/* Saldo Inicial (só na criação) */}
        {!isEditing && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Saldo Inicial"
              type="text"
              value={saldoFormatado}
              onChange={(e) => handleSaldoChange(e.target.value)}
              onBlur={(e) => handleSaldoChange(e.target.value)}
              error={!!errors.saldo_inicial}
              helperText={errors.saldo_inicial}
              placeholder="R$ 0,00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    R$
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        )}

        {/* Observações */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Observações"
            value={formData.observacoes}
            onChange={(e) => handleInputChange('observacoes', e.target.value)}
            placeholder="Informações adicionais sobre o caixa..."
          />
        </Grid>

        {/* Informações sobre caixa em edição */}
        {isEditing && caixa && (
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>Informações do Caixa:</strong>
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                <Chip 
                  label={`Tipo: ${caixa.tipo_vinculo}`} 
                  size="small" 
                  variant="outlined" 
                />
                <Chip 
                  label={`Saldo: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(caixa.saldo_atual)}`} 
                  size="small" 
                  variant="outlined" 
                />
                <Chip 
                  label={`Status: ${caixa.status}`} 
                  size="small" 
                  variant="outlined" 
                />
                {caixa.is_default && (
                  <Chip 
                    label="CAIXA DEFAULT" 
                    size="small" 
                    color="primary" 
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Criado em: {new Date(caixa.createdAt).toLocaleString()}
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>

      {/* Botões */}
      <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Caixa'}
        </Button>
      </Box>
    </Box>
  );
};

export default CaixaFinanceiroForm;
